import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getFirestore, doc, getDoc, getDocs, collection, setDoc, serverTimestamp, runTransaction,
} from 'firebase/firestore';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { STRIPE_PUBLISHABLE_KEY } from '../firebaseConfig';
import { recordFodycoinsEarn, recordFodycoinsSpend } from '../services/fodycoinsService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Swal from 'sweetalert2';
import './StorePage.css';

const db = getFirestore();
const functions = getFunctions();
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const cardElementStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: { color: '#9e2146' },
  },
};

const CheckoutForm = () => {
  const { slug } = useParams();
  const storeId = slug ? slug.split('-').pop() : '';
  const navigate = useNavigate();
  const location = useLocation();
  // Reservation mode: this checkout is a reservation PRE-ORDER — regular
  // order documents with orderType 'dine_in' / salesChannel 'reservation',
  // linked to the reservation via linkReservationPreorder after placement.
  const reservationContext = location.state?.reservationContext || null;
  const isReservationOrder = reservationContext != null;
  const { user, userData, guestLocation, userCollection } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [store, setStore] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);

  // Cart from localStorage
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(`ff_cart_${storeId}`);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Checkout state
  // Order type defaults to how the customer got here: reservation → dine_in;
  // else the browsing mode (navigation state first, persisted 'ff_mode'
  // fallback) — a Pick Up customer no longer lands on a Delivery checkout.
  const [orderType, setOrderType] = useState(() => {
    if (isReservationOrder) return 'dine_in';
    const browsed = location.state?.mode || localStorage.getItem('ff_mode');
    // 'reserve' never reaches a normal checkout (its store clicks go to the
    // booking flow) — guard anyway: without a reservation context, fall back.
    return browsed === 'delivery' ? 'delivery' : 'pickup';
  });
  const [orderNotes, setOrderNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  // Wallet pay (Apple Pay / Google Pay via Stripe Express Checkout). When the
  // wallet already charged, handlePlaceOrder skips the card form branch and
  // funnels into the same order-creation tail.
  const walletPayRef = useRef(null); // {paymentIntentId, cardBrand, cardLast4, chargedCents}
  const [walletBusy, setWalletBusy] = useState(false);
  const [walletAvailable, setWalletAvailable] = useState(false);
  const [tipIsPercent, setTipIsPercent] = useState(true);
  const [tipInput, setTipInput] = useState('');
  const [cardError, setCardError] = useState('');

  // Address
  const [deliveryAddress, setDeliveryAddress] = useState(() => {
    if (user && userData) {
      return guestLocation?.address || userData?.address || null;
    }
    return guestLocation?.address || null;
  });
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    label: 'Home',
  });
  const [saveAddressToProfile, setSaveAddressToProfile] = useState(true);

  // Uber Direct delivery quote (3PL fulfillment)
  const [deliveryQuote, setDeliveryQuote] = useState(null); // {fee, durationMinutes, quoteId, expiresAt}
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [quoteOutOfZone, setQuoteOutOfZone] = useState(false);

  // Uber Direct delivery fee (3PL fulfillment): quoted live when the store
  // has deliveryProvider == 'uber_direct' and the consumer set a delivery
  // address. The fee is what the merchant pays the courier — added to the
  // consumer's total like any delivery fee.
  // Default-ON while Uber Direct is the only provider (transparent for
  // merchant + consumer); a location opts out with deliveryProvider 'none'.
  // Declared HERE (before the quote useEffect below reads it) — `const` in
  // the temporal dead zone threw "Cannot access before initialization".
  const isUberDirectDelivery =
    orderType === 'delivery' &&
    (store?.deliveryProvider == null || store?.deliveryProvider === 'uber_direct');
  // The delivery fee only applies while the order type is 'delivery' —
  // switching to Pick Up must drop it from the total immediately. The quote
  // itself stays CACHED (Uber quotes expire in ~15 min) so switching back
  // to Delivery reuses it if still valid instead of re-quoting.
  const deliveryFee = orderType === 'delivery' ? (deliveryQuote?.fee || 0) : 0;

  // True when the cached quote exists and hasn't expired yet.
  const quoteStillValid = (() => {
    if (!deliveryQuote) return false;
    const exp = Date.parse(deliveryQuote.expiresAt || '');
    return isFinite(exp) && exp > Date.now();
  })();

  // ── Delivery zone (client-side pre-check) ────────────────────────────
  // Mirrors the server-side enforcement in getUberDeliveryQuote. Locations
  // without deliverySettings have NO zone restriction. Radius mode uses the
  // store's published coordinates + the customer's selected location; ZIP
  // mode uses the structured new-address zip or one extracted from the
  // free-text address.
  const deliverySettings = store?.deliverySettings || null;

  const extractZip = (text) => {
    const m = /\b(\d{5})(?:-\d{4})?\b/.exec(text || '');
    return m ? m[1] : null;
  };

  const haversineMiles = (lat1, lng1, lat2, lng2) => {
    const R = 3958.8; // Earth radius in miles
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(lat2 - lat1);
    const dLng = rad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  // true when the delivery address is definitely outside the zone (missing
  // coordinates/zip never block here — the server decides at quote time).
  const outsideDeliveryZone = (() => {
    if (orderType !== 'delivery' || !deliverySettings) return false;
    const zoneType = deliverySettings.zoneType === 'zipcodes' ? 'zipcodes' : 'radius';
    if (zoneType === 'zipcodes') {
      const zips = Array.isArray(deliverySettings.zipCodes) ? deliverySettings.zipCodes.map(String) : [];
      if (zips.length === 0) return false;
      const zip = newAddress.zip || extractZip(deliveryAddress);
      if (!zip) return false;
      return !zips.includes(String(zip).trim());
    }
    const radius = Number(deliverySettings.radiusMiles) > 0 ? Number(deliverySettings.radiusMiles) : 5;
    const restLat = Number(store?.latitude);
    const restLng = Number(store?.longitude);
    const dLat = Number(guestLocation?.lat);
    const dLng = Number(guestLocation?.lng);
    if (!isFinite(restLat) || !isFinite(restLng) || !isFinite(dLat) || !isFinite(dLng)) return false;
    return haversineMiles(restLat, restLng, dLat, dLng) > radius;
  })();

  const deliveryZoneLabel = (() => {
    if (!deliverySettings) return '';
    if (deliverySettings.zoneType === 'zipcodes') return 'specific ZIP codes';
    const radius = Number(deliverySettings.radiusMiles) > 0 ? Number(deliverySettings.radiusMiles) : 5;
    return `within ${radius % 1 === 0 ? radius : radius.toFixed(1)} miles of the restaurant`;
  })();

  // Fodyes
  const [userFodyes, setUserFodyes] = useState({ balance: 0, backingValue: 0 });
  const [useFodyes, setUseFodyes] = useState(false);

  // Promo code
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { promoId, code, name, type, value, discountAmount }
  const [promoError, setPromoError] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  useEffect(() => {
    if (!storeId) {
      navigate('/explore');
      return;
    }
    loadStoreData();
    loadUserFodyes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // Uber Direct: re-quote the delivery fee when the delivery address changes.
  // `store?.businessId` must be a dependency: if the address was pre-filled
  // from the profile, the first quote fired while the store was still null
  // (early return inside quoteDeliveryFee) and never re-fired — the fee never
  // appeared. Re-running when the store finishes loading fixes that.
  useEffect(() => {
    if (!isUberDirectDelivery) return;
    if (!store?.businessId) return;
    const t = setTimeout(() => quoteDeliveryFee(), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryAddress, isUberDirectDelivery, store?.businessId]);

  // Uber's JSON-string address format — structured from the new-address
  // form when available, else the free text as the street line (Uber
  // geocodes it).
  const buildDeliveryAddressJson = () => {
    if (newAddress.address) {
      return JSON.stringify({
        street_address: [newAddress.address],
        city: newAddress.city || '',
        state: newAddress.state || '',
        zip_code: newAddress.zip || '',
        country: 'US',
      });
    }
    return JSON.stringify({
      street_address: [(deliveryAddress || '').trim()],
      city: '',
      state: '',
      zip_code: '',
      country: 'US',
    });
  };

  const loadUserFodyes = async () => {
    if (!user?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const d = userDoc.data();
        setUserFodyes({
          balance: d.fodyesBalance || 0,
          backingValue: d.fodyesBackingValue || 0,
        });
      }
    } catch (e) {
      console.warn('Error loading user Fodyes:', e);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(`ff_cart_${storeId}`, JSON.stringify(cart));
    } catch {
      // ignore
    }
  }, [cart, storeId]);

  const loadStoreData = async () => {
    setLoading(true);
    try {
      const storeDoc = await getDoc(doc(db, 'publicStores', storeId));
      if (!storeDoc.exists()) {
        navigate('/explore');
        return;
      }
      const storeData = { id: storeDoc.id, ...storeDoc.data() };
      setStore(storeData);

      const optsSnap = await getDocs(collection(db, 'publicStores', storeId, 'productOptions'));
      const opts = optsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProductOptions(opts);
    } catch (error) {
      console.error('Error loading store:', error);
    } finally {
      setLoading(false);
    }
  };

  // Resolve workzoneId from category for each cart item
  const resolveWorkzoneIds = async () => {
    try {
      const catsSnap = await getDocs(collection(db, 'publicStores', storeId, 'categories'));
      const workzoneMap = {};
      catsSnap.docs.forEach((d) => {
        const data = d.data();
        const wz = data.workzonesByLocation?.[storeId] || null;
        workzoneMap[d.id] = wz;
      });
      return workzoneMap;
    } catch (e) {
      console.warn('Error resolving workzoneIds:', e);
      return {};
    }
  };

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  const cartSubtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      let unit = parseFloat(item.product.price) || 0;
      item.options.forEach((sel) => {
        const optDef = productOptions.find((o) => o.id === sel.optionId);
        if (optDef && optDef.options && optDef.options[sel.choiceIndex]) {
          unit += parseFloat(optDef.options[sel.choiceIndex].extraFee) || 0;
        }
      });
      return sum + unit * item.qty;
    }, 0);
  }, [cart, productOptions]);

  const taxRate = store?.taxRate || 0;
  const cartTax = cartSubtotal * (taxRate / 100);
  const calculatedTip = tipIsPercent
    ? cartSubtotal * ((parseFloat(tipInput) || 0) / 100)
    : (parseFloat(tipInput) || 0);

  // Convenience Fee (a.k.a. Processing Fee): extra % charged ONLY when
  // paying by card, configured per location. The Cloud Function
  // (createPaymentIntent) re-validates/applies this fee server-side too —
  // this is only used to preview the amount to the customer and to
  // persist it alongside the order for display later.
  const convenienceFee = useMemo(() => {
    if (paymentMethod !== 'card') return 0;
    const fee = store?.convenienceFee;
    if (!fee || fee.enabled !== true) return 0;
    const percent = parseFloat(fee.percent) || 0;
    if (percent <= 0) return 0;
    // Base includes the delivery fee: the backend surcharges the full
    // amount sent (which includes it), so the preview must match.
    return (cartSubtotal + cartTax + calculatedTip + deliveryFee) * (percent / 100);
  }, [paymentMethod, store, cartSubtotal, cartTax, calculatedTip, deliveryFee]);

  // Whether this location has the Convenience Fee toggle on at all
  // (regardless of payment method). Used to decide whether checkout even
  // needs to tokenize the card first to check credit-vs-debit — when off,
  // we use the original, simpler direct-confirm flow untouched.
  const convenienceFeeEnabledForLocation = store?.convenienceFee?.enabled === true;

  const cartTotal = cartSubtotal + cartTax + calculatedTip + convenienceFee;

  // Calculate Fodyes using restaurant config
  const storeFodyesConfig = useMemo(() => {
    const enabled = store?.enableFodyesRewards === true;
    const coinValue = parseFloat(store?.fodyesCoinValue) || 0;
    const returnPct = parseFloat(store?.fodyesReturnPercentage) || 0;
    if (!enabled || coinValue <= 0 || returnPct <= 0) {
      return { enabled: false, fodyesEarned: 0, backingValue: 0, coinValue: 0 };
    }
    const backingValue = cartTotal * (returnPct / 100);
    const fodyesEarned = Math.floor(backingValue / coinValue);
    return { enabled: true, fodyesEarned, backingValue, coinValue };
  }, [store, cartTotal]);
  const { fodyesEarned, backingValue, coinValue } = storeFodyesConfig;

  // Fodyes discount
  const minOrderForFodyes = parseFloat(store?.minOrderForFodyes) || 0;
  const maxFodyesDiscount = parseFloat(store?.maxFodyesDiscount) || 0;
  const canUseFodyes = storeFodyesConfig.enabled && cartTotal >= minOrderForFodyes;

  useEffect(() => {
    if (!canUseFodyes && useFodyes) {
      setUseFodyes(false);
    }
  }, [canUseFodyes, useFodyes]);

  // Whether this store accepts cash. Defaults to `true` while `store` is
  // still loading (or for older docs written before this field existed) to
  // match the same "opt-out, not opt-in" convention used everywhere else
  // this field is read (`data.acceptCash !== false`, see
  // stripe-web/LocationMaintenancePage.js and kiosk_data_service.dart).
  // Cash acceptance is per sales channel now (acceptCashByChannel): online
  // orders read the 'online' channel, reservation pre-orders read
  // 'reservation' ("pay at restaurant"). Locations saved before the field
  // existed fall back to the legacy global acceptCash.
  const cashChannel = isReservationOrder ? 'reservation' : 'online';
  // Cash is also hidden for Uber Direct delivery orders: the courier cannot
  // collect cash at the door. A merchant who opted out (deliveryProvider
  // 'none') delivers themselves, so cash-on-delivery stays available there.
  const hasCash = store
    ? (orderType === 'delivery' && isUberDirectDelivery
        ? false
        : (store.acceptCashByChannel?.[cashChannel] ?? store.acceptCash !== false))
    : true;

  // Public checkout used to always default to (and allow) "Cash" even when
  // the merchant had turned off "Accept Cash Payments" for this location —
  // `acceptCash` was never read here at all. If it turns out to be
  // disabled once the store loads, fall back to card automatically.
  useEffect(() => {
    if (!hasCash && paymentMethod === 'cash') {
      setPaymentMethod('card');
    }
  }, [hasCash, paymentMethod]);

  const fodyesDiscount = useMemo(() => {
    if (!useFodyes || !canUseFodyes || userFodyes.backingValue <= 0) return 0;
    let discount = Math.min(userFodyes.backingValue, cartTotal);
    if (maxFodyesDiscount > 0) {
      discount = Math.min(discount, maxFodyesDiscount);
    }
    return discount;
  }, [useFodyes, canUseFodyes, userFodyes.backingValue, cartTotal, maxFodyesDiscount]);
  const promoDiscount = appliedPromo?.discountAmount || 0;
  const finalTotal = cartTotal + deliveryFee - fodyesDiscount - promoDiscount;

  // Quote the Uber Direct delivery fee for the current delivery address.
  // The address goes in Uber's JSON-string format; structured parts from the
  // new-address form when available, else the free text as the street line.
  // The customer's selected location (guestLocation lat/lng) rides along so
  // the backend can enforce the location's delivery zone (radius / ZIP list).
  const quoteDeliveryFee = async (addressOverride) => {
    if (!isUberDirectDelivery) return;
    const addressText = (addressOverride || deliveryAddress || '').trim();
    if (!addressText) return;
    if (!store?.businessId) return;

    // Client-side zone pre-check: instant feedback without a network call.
    if (outsideDeliveryZone) {
      setDeliveryQuote(null);
      setQuoteOutOfZone(true);
      setQuoteError(`This store doesn't deliver to that address — delivery is only available ${deliveryZoneLabel}.`);
      return;
    }

    setQuoteLoading(true);
    setQuoteError('');
    setQuoteOutOfZone(false);
    try {
      const dropoff = newAddress.address
        ? JSON.stringify({
            street_address: [newAddress.address],
            city: newAddress.city || '',
            state: newAddress.state || '',
            zip_code: newAddress.zip || '',
            country: 'US',
          })
        : JSON.stringify({
            street_address: [addressText],
            city: '',
            state: '',
            zip_code: extractZip(addressText) || '',
            country: 'US',
          });
      const fn = httpsCallable(functions, 'getUberDeliveryQuote');
      const payload = {
        businessId: store.businessId,
        locationId: storeId,
        dropoffAddress: dropoff,
      };
      if (guestLocation?.lat != null) payload.dropoffLatitude = guestLocation.lat;
      if (guestLocation?.lng != null) payload.dropoffLongitude = guestLocation.lng;
      const { data } = await fn(payload);
      // Authoritative zone verdict from the server (covers cases the local
      // pre-check can't decide, e.g. missing coordinates).
      if (data?.outOfZone === true) {
        setDeliveryQuote(null);
        setQuoteOutOfZone(true);
        setQuoteError(data.message || `This store doesn't deliver to that address — delivery is only available ${deliveryZoneLabel}.`);
        return;
      }
      setDeliveryQuote(data);
    } catch (e) {
      console.warn('Delivery quote failed:', e);
      setDeliveryQuote(null);
      setQuoteOutOfZone(false);
      setQuoteError('Delivery quote unavailable — the store will arrange delivery manually.');
    } finally {
      setQuoteLoading(false);
    }
  };

  // Line items for promo scoping (productId/categoryId/lineTotal) — mirrors
  // the same unit-price computation used by cartSubtotal above.
  const buildPromoLineItems = () => cart.map((item) => {
    let unit = parseFloat(item.product.price) || 0;
    item.options.forEach((sel) => {
      const optDef = productOptions.find((o) => o.id === sel.optionId);
      if (optDef && optDef.options && optDef.options[sel.choiceIndex]) {
        unit += parseFloat(optDef.options[sel.choiceIndex].extraFee) || 0;
      }
    });
    return {
      productId: item.product.id,
      categoryId: item.product.categoryId || null,
      lineTotal: unit * item.qty,
    };
  });

  const handleApplyPromo = async () => {
    const code = promoCodeInput.trim();
    if (!code) return;
    setApplyingPromo(true);
    setPromoError('');
    try {
      const validatePromo = httpsCallable(functions, 'validatePromoCode');
      const { data } = await validatePromo({
        businessId: store?.businessId,
        locationId: storeId,
        code,
        salesChannel: 'online orders',
        items: buildPromoLineItems(),
        cartSubtotal,
        commit: false,
      });
      if (data.success) {
        setAppliedPromo({ ...data, code: code.toUpperCase() });
      } else {
        setAppliedPromo(null);
        setPromoError(data.message || 'This promo code is not valid.');
      }
    } catch (e) {
      console.error('Error validating promo code:', e);
      setPromoError('Could not validate promo code. Please try again.');
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCodeInput('');
    setPromoError('');
  };

  const canPlaceOrder = () => {
    if (cart.length === 0) return false;
    if (orderType === 'delivery' && !deliveryAddress) return false;
    // Delivery zone enforcement: never let an out-of-zone delivery order
    // through (the zone message is already visible near the totals).
    if (orderType === 'delivery' && (outsideDeliveryZone || quoteOutOfZone)) return false;
    // Defensive re-check: never let a stale `paymentMethod` place a cash
    // order at a store that doesn't accept cash, even if the disabled
    // button below somehow couldn't have been clicked to get here.
    if (paymentMethod === 'cash' && !hasCash) return false;
    if (paymentMethod === 'card') {
      return stripe && elements && !placingOrder;
    }
    return true;
  };

  const updateQty = (idx, delta) => {
    setCart((prev) => {
      const updated = [...prev];
      const newQty = updated[idx].qty + delta;
      if (newQty <= 0) {
        updated.splice(idx, 1);
      } else {
        updated[idx] = { ...updated[idx], qty: newQty };
      }
      return updated;
    });
  };

  const removeItem = (idx) => {
    setCart((prev) => prev.filter((_, i) => i !== idx));
  };

  const formatCurrency = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return `$${num.toFixed(2)}`;
  };

  const handleSaveNewAddress = () => {
    const full = `${newAddress.address}, ${newAddress.city}, ${newAddress.state} ${newAddress.zip}`.trim();
    if (!newAddress.address || !newAddress.city || !newAddress.state || !newAddress.zip) {
      Swal.fire('Incomplete Address', 'Please fill all address fields.', 'warning');
      return;
    }
    setDeliveryAddress(full);
    setShowNewAddress(false);
  };

  // Apple Pay / Google Pay (Stripe Express Checkout). The wallet authorizes
  // in its sheet; we then run the SAME fee-aware server flow as a typed card
  // (paymentMethodId → backend checks funding → convenience fee when it
  // applies → creates+confirms the PI). Then handlePlaceOrder builds the
  // order docs exactly as a card payment.
  const handleWalletConfirm = async (event) => {
    if (!stripe || !elements) return;
    setWalletBusy(true);
    setCardError('');
    try {
      // 1. Lock the wallet session into Stripe Elements
      const { error: submitError } = await elements.submit();
      if (submitError) throw submitError;

      // 2. Materialize the wallet payment method (funding readable → the
      //    convenience-fee logic treats it exactly like a typed card)
      const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({ elements });
      if (pmError || !pm) throw (pmError || new Error('Could not read the wallet card'));

      // 3. Same backend call as the card flow (server creates AND confirms)
      const functions = getFunctions();
      const createPi = httpsCallable(functions, 'createPaymentIntent');
      const { data } = await createPi({
        amount: Math.round(finalTotal * 100),
        currency: 'usd',
        businessId: store?.businessId,
        stripeCustomerId: userData?.stripeCustomerId || null,
        source: 'public-web-wallet',
        locationId: storeId,
        paymentMethod: 'card',
        paymentMethodId: pm.id,
        ...(isUberDirectDelivery && deliveryFee > 0 ? { deliveryFeeAmount: Math.round(deliveryFee * 100) } : {}),
      });
      if (!data.success || !data.clientSecret) throw new Error(data.error || 'Failed to create payment intent');

      let finalStatus = data.status;
      if (finalStatus === 'requires_action') {
        const { paymentIntent: handled, error: actionError } = await stripe.handleCardAction(data.clientSecret);
        if (actionError) throw actionError;
        finalStatus = handled.status;
      }
      if (finalStatus !== 'succeeded') throw new Error(`Payment status: ${finalStatus}`);

      // 4. Arm the paid ref and let the normal order flow build the docs
      walletPayRef.current = {
        paymentIntentId: data.paymentIntentId,
        cardBrand: pm.card?.brand || null,
        cardLast4: pm.card?.last4 || null,
        chargedCents: typeof data.amount === 'number' ? data.amount : null,
      };
      await handlePlaceOrder();
    } catch (e) {
      console.error('Wallet payment failed:', e);
      setCardError(e.message || 'Wallet payment failed');
    } finally {
      setWalletBusy(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!canPlaceOrder()) return;
    setPlacingOrder(true);
    setShowProcessing(true);
    setCardError('');

    let stripePaymentIntentId = null;
    let cardBrand = null;
    let cardLast4 = null;
    let purchaseDocId = null; // ID of the user's purchase doc (for redirect)

    // Re-validate + atomically commit the promo code (increments its usage
    // counter and records a redemption) BEFORE charging anything. This closes
    // the race window between the "Apply" preview and actually placing the
    // order, and guarantees the amount charged matches a discount that was
    // truly still valid/available. We use a local variable (not the
    // `appliedPromo` state) for the rest of this function so the amount
    // charged/persisted can never drift from what the commit call approved.
    let effectivePromoDiscount = 0;
    let committedPromoId = null;
    let committedPromoCode = null;
    if (appliedPromo) {
      try {
        const validatePromo = httpsCallable(functions, 'validatePromoCode');
        const { data: promoResult } = await validatePromo({
          businessId: store?.businessId,
          locationId: storeId,
          code: appliedPromo.code,
          salesChannel: 'online orders',
          items: buildPromoLineItems(),
          cartSubtotal,
          commit: true,
        });
        if (!promoResult.success) {
          Swal.fire('Promo Code Issue', promoResult.message || 'This promo code could not be applied. Please remove it and try again.', 'warning');
          setAppliedPromo(null);
          setPlacingOrder(false);
          setShowProcessing(false);
          return;
        }
        effectivePromoDiscount = promoResult.discountAmount;
        committedPromoId = promoResult.promoId;
        committedPromoCode = appliedPromo.code;
      } catch (promoErr) {
        console.error('Error committing promo code:', promoErr);
        Swal.fire('Error', 'Could not apply promo code. Please try again.', 'error');
        setPlacingOrder(false);
        setShowProcessing(false);
        return;
      }
    }
    let effectiveFinalTotal = cartTotal + deliveryFee - fodyesDiscount - effectivePromoDiscount;
    let effectiveConvenienceFee = convenienceFee;

    try {
      if (paymentMethod === 'card') {
        // Wallet already charged (Apple Pay / Google Pay via the Express
        // Checkout element): skip the card form entirely and reuse the
        // backend-confirmed intent — the order tail is identical.
        if (walletPayRef.current) {
          const w = walletPayRef.current;
          walletPayRef.current = null;
          stripePaymentIntentId = w.paymentIntentId;
          cardBrand = w.cardBrand;
          cardLast4 = w.cardLast4;
          if (typeof w.chargedCents === 'number') effectiveFinalTotal = w.chargedCents / 100;
        } else {
        if (!stripe || !elements) {
          throw new Error('Stripe not ready. Please wait.');
        }
        const cardEl = elements.getElement(CardElement);
        if (!cardEl) {
          throw new Error('Card element not found');
        }

        if (convenienceFeeEnabledForLocation) {
          // Tokenize first (so we can check card.funding — credit vs debit
          // — server-side before deciding the final amount), then let the
          // backend create AND confirm the PaymentIntent. Only taken when
          // this location actually has the fee turned on — when it's off,
          // the original direct-confirm flow below runs completely
          // untouched, so disabling the fee guarantees zero change to how
          // cards are charged, not just to the amount.
          const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardEl,
            billing_details: {
              name: userData?.name || user?.displayName || 'Guest',
            },
          });
          if (pmError) {
            setCardError(pmError.message || 'Invalid card details');
            setPlacingOrder(false);
            return;
          }

          const createPi = httpsCallable(functions, 'createPaymentIntent');
          const { data } = await createPi({
            amount: Math.round(effectiveFinalTotal * 100),
            currency: 'usd',
            businessId: store?.businessId,
            stripeCustomerId: userData?.stripeCustomerId || null,
            source: 'public-web',
            // NOTE: no applicationFeePercent — the platform commission is
            // read server-side from the location's platformFeePercent
            // (never trust the client with the marketplace's cut).
            locationId: storeId,
            paymentMethod: 'card',
            paymentMethodId: pm.id,
            // Uber Direct: the delivery fee stays on the platform (FodyFee's
            // Uber account pays the courier) — the backend adds it to the
            // application fee instead of transferring it to the restaurant.
            ...(isUberDirectDelivery && deliveryFee > 0
              ? { deliveryFeeAmount: Math.round(deliveryFee * 100) }
              : {}),
          });
          if (!data.success || !data.clientSecret) {
            throw new Error(data.error || 'Failed to create payment intent');
          }

          // The backend is the authority on the Convenience Fee (it
          // checked card.funding — credit vs debit — and only applies the
          // fee for credit cards) — reconcile our locally-previewed
          // total/fee with what it actually charged, so what gets
          // persisted always matches the real Stripe amount.
          if (typeof data.amount === 'number') {
            effectiveFinalTotal = data.amount / 100;
          }
          if (typeof data.convenienceFeeAmount === 'number') {
            effectiveConvenienceFee = data.convenienceFeeAmount / 100;
          }

          let finalStatus = data.status;
          if (finalStatus === 'requires_action') {
            // 3D Secure or similar challenge — the PaymentIntent was already
            // created with this payment method attached server-side, so we
            // only need to complete the authentication challenge here.
            const { paymentIntent: handled, error: actionError } = await stripe.handleCardAction(data.clientSecret);
            if (actionError) {
              setCardError(actionError.message || 'Card authentication failed');
              setPlacingOrder(false);
              return;
            }
            finalStatus = handled.status;
          }

          if (finalStatus !== 'succeeded') {
            setCardError(`Payment status: ${finalStatus}`);
            setPlacingOrder(false);
            return;
          }
          stripePaymentIntentId = data.paymentIntentId;

          // Extract card details from the tokenized payment method (already
          // available before charging — no need to inspect the charge object).
          const pmDetails = pm?.card;
          if (pmDetails) {
            cardBrand = pmDetails.brand || null;
            cardLast4 = pmDetails.last4 || null;
          }
        } else {
          // Original flow, unchanged: create the PaymentIntent first and
          // let Stripe collect + confirm the card directly. Used whenever
          // this location doesn't have the Convenience Fee enabled.
          const createPi = httpsCallable(functions, 'createPaymentIntent');
          const { data } = await createPi({
            amount: Math.round(effectiveFinalTotal * 100),
            currency: 'usd',
            businessId: store?.businessId,
            stripeCustomerId: userData?.stripeCustomerId || null,
            source: 'public-web',
            applicationFeePercent: 5,
          });
          if (!data.success || !data.clientSecret) {
            throw new Error(data.error || 'Failed to create payment intent');
          }

          const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
            data.clientSecret,
            {
              payment_method: {
                card: cardEl,
                billing_details: {
                  name: userData?.name || user?.displayName || 'Guest',
                },
              },
            }
          );

          if (confirmError) {
            setCardError(confirmError.message || 'Payment failed');
            setPlacingOrder(false);
            return;
          }
          if (paymentIntent.status !== 'succeeded') {
            setCardError(`Payment status: ${paymentIntent.status}`);
            setPlacingOrder(false);
            return;
          }
          stripePaymentIntentId = paymentIntent.id;

          const pmDetails = paymentIntent?.charges?.data?.[0]?.payment_method_details?.card;
          if (pmDetails) {
            cardBrand = pmDetails.brand || null;
            cardLast4 = pmDetails.last4 || null;
          }
        }
        }
      }

      // Reservation orders share ONE id across the public order, the
      // merchant sale (both paths) and the purchase doc, so the backend can
      // update all of them by that single id (link/pay/refund flows).
      const reservationSaleId = isReservationOrder
        ? doc(collection(db, 'publicStores', storeId, 'orders')).id
        : null;
      const orderRef = isReservationOrder
        ? doc(collection(db, 'publicStores', storeId, 'orders'), reservationSaleId)
        : doc(collection(db, 'publicStores', storeId, 'orders'));
      const workzoneMap = await resolveWorkzoneIds();

      const items = cart.map((item) => {
        let unitPrice = parseFloat(item.product.price) || 0;
        const opts = item.options.map((sel) => {
          const optDef = productOptions.find((o) => o.id === sel.optionId);
          const choice = optDef?.options?.[sel.choiceIndex];
          const extra = parseFloat(choice?.extraFee) || 0;
          unitPrice += extra;
          return {
            optionId: sel.optionId,
            optionName: optDef?.name || '',
            name: choice?.name || '',
            extraFee: extra,
          };
        });
        const categoryId = item.product.categoryId || null;
        return {
          productId: item.product.id,
          productName: item.product.name,
          unitPrice,
          qty: item.qty,
          options: opts,
          instructions: item.instructions || '',
          categoryId,
          categoryName: item.product.categoryName || '',
          workzoneId: workzoneMap[categoryId] || null,
        };
      });

      const confirmationNumber = orderRef.id.slice(-6).toUpperCase();

      // Generate guest number
      const businessId = store?.businessId;
      let guestNumber = null;
      if (businessId) {
        try {
          const locRef = doc(db, 'users', businessId, 'locations', storeId);
          guestNumber = await runTransaction(db, async (transaction) => {
            const locDoc = await transaction.get(locRef);
            let ordersPosition = locDoc.data()?.ordersPosition || 0;
            const ordersCorrelative = locDoc.data()?.ordersCorrelative || 1;
            ordersPosition++;
            // Restart point = raw ordersCorrelative value (the location
            // editor's label means that number — old * 100 never applied).
            if (ordersPosition > ordersCorrelative) ordersPosition = 1;
            transaction.update(locRef, { ordersPosition });
            return ordersPosition.toString().padStart(4, '0');
          });
        } catch (e) {
          console.error('Error generating guest number:', e);
        }
      }

      // A cash order here has no payment guarantee yet — nobody's
      // validated it was actually paid — so it always starts 'pending'
      // and needs a cashier/manager to explicitly accept it before the
      // kitchen sees it (see pos-machine's pending-orders alert). A card
      // order is already charged, so it goes straight in with the
      // store's configured fulfillment status, UNLESS the merchant has
      // "Confirm All Orders" enabled (`confirmAllOrders`), in which case
      // every online order — cash or card — starts 'pending' regardless.
      const confirmAllOrders = store?.confirmAllOrders === true;
      // Reservation pre-orders ALWAYS start 'pending' — the kitchen only
      // fires when the party is seated at the restaurant (check-in).
      const defaultFs = isReservationOrder
        ? 'pending'
        : confirmAllOrders
          ? 'pending'
          : (paymentMethod === 'cash' ? 'pending' : (store?.defaultFulfillmentStatus || 'pending'));

      const orderData = {
        consumerId: user?.uid || null,
        consumerEmail: user?.email || null,
        consumerName: userData?.name || user?.displayName || 'Guest',
        consumerPhone: userData?.phone || null,
        items,
        subtotal: cartSubtotal,
        tax: cartTax,
        tip: calculatedTip,
        convenienceFee: effectiveConvenienceFee,
        total: effectiveFinalTotal,
        fodyesDiscount: fodyesDiscount || null,
        promoId: committedPromoId || null,
        promoCode: committedPromoCode || null,
        promoDiscount: effectivePromoDiscount || null,
        orderType,
        paymentMethod,
        paymentStatus: paymentMethod === 'card' ? 'paid' : 'pending',
        stripePaymentIntentId: stripePaymentIntentId || null,
        cardBrand: cardBrand || null,
        cardLast4: cardLast4 || null,
        status: paymentMethod === 'card' ? 'complete' : 'open',
        fulfillmentStatus: defaultFs,
        ...(isReservationOrder ? { reservationId: reservationContext.reservationId, salesChannel: 'reservation' } : {}),
        notes: orderNotes.trim(),
        deliveryAddress: orderType === 'delivery' ? (deliveryAddress || null) : null,
        deliveryLat: orderType === 'delivery' ? (guestLocation?.lat || null) : null,
        deliveryLng: orderType === 'delivery' ? (guestLocation?.lng || null) : null,
        // Uber Direct (3PL fulfillment) — the dispatch trigger reads these.
        ...(isUberDirectDelivery ? {
          deliveryProvider: 'uber_direct',
          deliveryFee: deliveryFee > 0 ? deliveryFee : null,
          deliveryAddressJson: buildDeliveryAddressJson(),
        } : {}),
        businessId: store?.businessId || null,
        storeName: store?.locationName || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(orderRef, orderData);

      // Stamp the two online fees onto the sale (FodyFee application fee +
      // Stripe's processing fee on the merchant) — server-side, fire-and-
      // forget; never blocks the customer's confirmation screen.
      if (stripePaymentIntentId) {
        httpsCallable(getFunctions(), 'stampOnlinePaymentFees')({ paymentIntentId: stripePaymentIntentId })
          .catch((e) => console.warn('[Checkout] fee stamp failed (non-blocking):', e.message));
      }

      let saleRef = null;
      if (businessId) {
        try {
          saleRef = isReservationOrder
            ? doc(db, 'users', businessId, 'locations', storeId, 'sales', reservationSaleId)
            : doc(collection(db, 'users', businessId, 'locations', storeId, 'sales'));
          const nowTs = serverTimestamp();
          const saleData = {
            salesChannel: isReservationOrder ? 'reservation' : 'online orders',
            customerId: user?.uid || null,
            fodyesUid: user?.uid || null,
            customerName: orderData.consumerName,
            customerEmail: orderData.consumerEmail,
            customerPhone: orderData.consumerPhone,
            itemsQty: items.reduce((s, i) => s + i.qty, 0),
            subtotal: cartSubtotal,
            tax: cartTax,
            tip: calculatedTip,
            tipPercent: tipIsPercent ? (parseFloat(tipInput) || 0) : 0,
            tipIsPercent,
            convenienceFee: effectiveConvenienceFee,
            total: effectiveFinalTotal,
            fodyesDiscount: fodyesDiscount || null,
            promoId: committedPromoId || null,
            promoCode: committedPromoCode || null,
            discount: effectivePromoDiscount || 0,
            orderType: isReservationOrder ? 'dine_in' : (orderType === 'delivery' ? 'Delivery' : 'Pick Up'),
            paymentType: paymentMethod,
            paymentStatus: paymentMethod === 'card' ? 'paid' : 'pending',
            stripePaymentIntentId: stripePaymentIntentId || null,
            cardBrand: cardBrand || null,
            cardLast4: cardLast4 || null,
            status: paymentMethod === 'card' ? 'completed' : 'open',
            orderStatus: paymentMethod === 'card' ? 'completed' : 'open',
            fulfillmentStatus: defaultFs,
            ...(isReservationOrder ? { reservationId: reservationContext.reservationId } : {}),
            // Uber Direct (3PL fulfillment) — the dispatch trigger reads
            // these from the location sale doc when it turns 'ready'.
            ...(orderType === 'delivery' ? {
              deliveryAddress: deliveryAddress || null,
              deliveryLat: guestLocation?.lat || null,
              deliveryLng: guestLocation?.lng || null,
            } : {}),
            ...(isUberDirectDelivery ? {
              deliveryProvider: 'uber_direct',
              deliveryFee: deliveryFee > 0 ? deliveryFee : null,
              deliveryAddressJson: buildDeliveryAddressJson(),
            } : {}),
            notes: orderNotes.trim(),
            confirmationNumber,
            guestNumber,
            locationId: storeId,
            locationName: store?.locationName || '',
            businessName: store?.locationName || '',
            orderDate: nowTs,
            createdAt: nowTs,
            updatedAt: nowTs,
            // closeAt only for completed (card) orders so POS and web both show "Closed"
            ...(paymentMethod === 'card' ? { closeAt: nowTs } : {}),
            sourceSaleId: orderRef.id,
            // Inline items so POS Machine can print immediately even before subcollection is visible
            items: items.map((it) => ({
              productId: it.productId,
              productName: it.productName,
              price: it.unitPrice,
              qty: it.qty,
              total: it.unitPrice * it.qty,
              options: it.options,
              instructions: it.instructions || '',
              workzoneId: it.workzoneId || null,
              categoryId: it.categoryId || null,
              categoryName: it.categoryName || '',
              fulfillmentStatus: defaultFs,
            })),
          };

          // Write items subcollection FIRST so POS listener sees them when it reads the parent
          const itemWrites = items.map((it) =>
            setDoc(doc(collection(db, 'users', businessId, 'locations', storeId, 'sales', saleRef.id, 'items')), {
              productId: it.productId,
              productName: it.productName,
              price: it.unitPrice,
              qty: it.qty,
              total: it.unitPrice * it.qty,
              options: it.options,
              instructions: it.instructions || '',
              workzoneId: it.workzoneId || null,
              categoryId: it.categoryId || null,
              categoryName: it.categoryName || '',
              fulfillmentStatus: defaultFs,
              createdAt: nowTs,
              updatedAt: nowTs,
            })
          );
          await Promise.all(itemWrites);
          await setDoc(saleRef, saleData);

          // Mirror to users/{uid}/sales so AdminDashboard and other consumers see it
          try {
            const rootSaleRef = doc(db, 'users', businessId, 'sales', saleRef.id);
            await setDoc(rootSaleRef, { ...saleData, items: null }); // avoid duplicating large inline array
            const rootItemWrites = items.map((it) =>
              setDoc(doc(collection(db, 'users', businessId, 'sales', saleRef.id, 'items')), {
                productId: it.productId,
                productName: it.productName,
                price: it.unitPrice,
                qty: it.qty,
                total: it.unitPrice * it.qty,
                options: it.options,
                instructions: it.instructions || '',
                workzoneId: it.workzoneId || null,
                categoryId: it.categoryId || null,
                categoryName: it.categoryName || '',
                fulfillmentStatus: defaultFs,
                createdAt: nowTs,
                updatedAt: nowTs,
              })
            );
            await Promise.all(rootItemWrites);
          } catch (mirrorErr) {
            console.warn('Could not mirror sale to root sales collection:', mirrorErr);
          }

          // Auto-add consumer to merchant's customer list if not already there
          if (user?.uid && businessId) {
            try {
              const merchantCustomerRef = doc(db, 'users', businessId, 'customers', user.uid);
              const merchantCustomerSnap = await getDoc(merchantCustomerRef);
              if (!merchantCustomerSnap.exists()) {
                await setDoc(merchantCustomerRef, {
                  name: orderData.consumerName || user.displayName || 'Customer',
                  email: orderData.consumerEmail || user.email || null,
                  phone: orderData.consumerPhone || null,
                  address: orderType === 'delivery' ? (deliveryAddress || null) : null,
                  city: null,
                  state: null,
                  zipCode: null,
                  country: null,
                  longitude: orderType === 'delivery' ? (guestLocation?.lng || null) : null,
                  latitude: orderType === 'delivery' ? (guestLocation?.lat || null) : null,
                  stripeCustomerId: userData?.stripeCustomerId || null,
                  createdAt: serverTimestamp(),
                });
              }
            } catch (customerErr) {
              console.warn('Could not auto-add customer to merchant list:', customerErr);
            }
          }

          if (user?.uid) {
            const purchaseRef = isReservationOrder
              ? doc(db, userCollection, user.uid, 'purchases', reservationSaleId)
              : doc(collection(db, userCollection, user.uid, 'purchases'));
            purchaseDocId = purchaseRef.id;
            const purchaseData = {
              saleId: saleRef.id,
              businessId,
              locationId: storeId,
              storeName: store?.locationName || '',
              consumerId: user.uid,
              consumerName: orderData.consumerName,
              consumerEmail: orderData.consumerEmail,
              consumerPhone: orderData.consumerPhone,
              items,
              subtotal: cartSubtotal,
              tax: cartTax,
              tip: calculatedTip,
              convenienceFee: effectiveConvenienceFee,
              total: effectiveFinalTotal,
              fodyesDiscount: fodyesDiscount || null,
              promoId: committedPromoId || null,
              promoCode: committedPromoCode || null,
              promoDiscount: effectivePromoDiscount || null,
              orderType,
              paymentMethod,
              stripePaymentIntentId: stripePaymentIntentId || null,
              cardBrand: cardBrand || null,
              cardLast4: cardLast4 || null,
              status: paymentMethod === 'card' ? 'complete' : 'open',
              fulfillmentStatus: defaultFs,
              ...(isReservationOrder ? { reservationId: reservationContext.reservationId } : {}),
              notes: orderNotes.trim(),
              deliveryAddress: orderType === 'delivery' ? (deliveryAddress || null) : null,
              deliveryLat: orderType === 'delivery' ? (guestLocation?.lat || null) : null,
              deliveryLng: orderType === 'delivery' ? (guestLocation?.lng || null) : null,
              ...(isUberDirectDelivery ? {
                deliveryProvider: 'uber_direct',
                deliveryFee: deliveryFee > 0 ? deliveryFee : null,
                deliveryTrackingUrl: null,
              } : {}),
              confirmationNumber,
              guestNumber,
              publicOrderId: orderRef.id,
              orderDate: serverTimestamp(),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };
            await setDoc(purchaseRef, purchaseData);

            // Save new address to profile if user opted in
            if (saveAddressToProfile && showNewAddress && newAddress.address) {
              try {
                const addrRef = doc(collection(db, userCollection, user.uid, 'addresses'));
                await setDoc(addrRef, {
                  address: newAddress.address,
                  city: newAddress.city,
                  state: newAddress.state,
                  zip: newAddress.zip,
                  label: newAddress.label || 'Home',
                  isDefault: false,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                });
              } catch (addrErr) {
                console.warn('Could not save new address to profile:', addrErr);
              }
            }
          }
        } catch (merchantErr) {
          console.error('Error saving to merchant sales:', merchantErr);
        }
      }

      // Record Fodyes earned on the blockchain ledger
      if (user?.uid && fodyesEarned > 0) {
        try {
          await recordFodycoinsEarn(
            user.uid,
            fodyesEarned,
            `Order #${confirmationNumber} · ${store?.locationName || 'Store'}`,
            {
              orderId: orderRef.id,
              storeId,
              businessId: store?.businessId,
              saleId: saleRef?.id || null,
              total: cartTotal,
              confirmationNumber,
              guestNumber,
            },
            backingValue,
            coinValue,
            cartTotal
          );
        } catch (ledgerErr) {
          console.warn('Could not record Fodycoins block:', ledgerErr);
        }
      }

      // Record Fodyes spent on the blockchain ledger
      if (user?.uid && fodyesDiscount > 0) {
        try {
          await recordFodycoinsSpend(
            user.uid,
            fodyesDiscount,
            `Used Fodyes on Order #${confirmationNumber} · ${store?.locationName || 'Store'}`,
            {
              orderId: orderRef.id,
              storeId,
              businessId: store?.businessId,
              saleId: saleRef?.id || null,
              discount: fodyesDiscount,
              confirmationNumber,
              guestNumber,
            },
            coinValue,
            cartTotal
          );
        } catch (ledgerErr) {
          console.warn('Could not record Fodycoins spend block:', ledgerErr);
        }
      }

      // Send receipt email
      if (user?.email && saleRef) {
        try {
          const sendReceipt = httpsCallable(functions, 'sendReceiptEmail');
          await sendReceipt({
            saleId: saleRef.id,
            customerEmail: user.email,
            customerName: userData?.name || user.displayName || 'Customer',
            locationName: store?.locationName || 'Store',
            items: items.map((it) => ({
              productName: it.productName,
              qty: it.qty,
              price: it.unitPrice,
              total: it.unitPrice * it.qty,
              options: it.options?.map((o) => o.name).filter(Boolean),
              instructions: it.instructions,
            })),
            subtotal: cartSubtotal,
            tax: cartTax,
            tip: calculatedTip,
            convenienceFee: effectiveConvenienceFee,
            discount: (fodyesDiscount || 0) + (effectivePromoDiscount || 0),
            total: effectiveFinalTotal,
          });
        } catch (emailErr) {
          console.warn('Could not send receipt email:', emailErr);
        }
      }

      // Clear cart ONLY after all async work is done and just before showing success
      setCart([]);
      localStorage.removeItem(`ff_cart_${storeId}`);

      // ── Reservation link (reservation mode only) ─────────────
      if (isReservationOrder) {
        try {
          const linkFn = httpsCallable(functions, 'linkReservationPreorder');
          await linkFn({
            businessId,
            locationId: storeId,
            reservationId: reservationContext.reservationId,
            saleId: saleRef?.id || reservationSaleId,
            items: items.map((it) => ({
              productId: it.productId,
              productName: it.productName,
              qty: it.qty,
              price: it.unitPrice,
              total: it.unitPrice * it.qty,
              options: (it.options || []).map((o) => o.name).filter(Boolean),
              instructions: it.instructions || '',
              categoryId: it.categoryId || null,
              categoryName: it.categoryName || null,
              workzoneId: it.workzoneId || null,
            })),
            subtotal: cartSubtotal,
            tax: cartTax,
            tip: calculatedTip,
            convenienceFee: effectiveConvenienceFee,
            total: effectiveFinalTotal,
            paymentStatus: paymentMethod === 'card' ? 'completed' : 'open',
            stripePaymentIntentId: stripePaymentIntentId || null,
            cardBrand: cardBrand || null,
            cardLast4: cardLast4 || null,
          });
        } catch (linkErr) {
          // The order exists and carries reservationId — staff can still see
          // it. Surface a soft warning instead of failing the whole order.
          console.warn('Reservation link failed (order placed):', linkErr);
          Swal.fire({
            icon: 'warning',
            title: 'Order placed',
            text: 'Your order was placed, but linking it to your reservation failed — the restaurant can still see it.',
            confirmButtonColor: '#ff5722',
          });
        }
      }

      Swal.fire({
        title: isReservationOrder ? 'Pre-order placed!' : 'Order Placed!',
        text: isReservationOrder
          ? `Your pre-order #${confirmationNumber} was added to your reservation.`
          : `Your order #${confirmationNumber}${guestNumber ? ` · Guest #${guestNumber}` : ''} has been sent to ${store?.locationName || 'the store'}.${fodyesEarned > 0 ? ` You earned ${fodyesEarned} Fodyes!` : ''}`,
        icon: 'success',
        didOpen: () => {
          setShowProcessing(false);
        },
      }).then(() => {
        if (isReservationOrder) {
          navigate(`/reservation/${reservationContext.reservationId}`);
        } else {
          // Redirect to the user's purchase document so OrderTrackingPage can find it
          const redirectId = purchaseDocId || orderRef.id;
          navigate(`/order/${redirectId}`);
        }
      });
    } catch (error) {
      console.error('Error placing order:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to place order.',
        icon: 'error',
        didOpen: () => {
          setShowProcessing(false);
        },
      });
    } finally {
      setPlacingOrder(false);
    }
  };

  if (loading) {
    return (
      <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1 }} className="ff-loading-stores">
          <div className="ff-spinner"></div>
          <p>Loading checkout...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!store) return null;

  const primaryColor = store?.design?.primaryColor || '#ff5722';
  const secondaryColor = store?.design?.secondaryColor || '#ff8a50';

  // Empty cart screen
  if (cart.length === 0) {
    return (
      <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
        <Header />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div className="text-center">
            <i className="fas fa-shopping-basket fa-3x mb-3" style={{ color: '#ccc' }}></i>
            <h4 className="font-weight-bold mb-2">Your cart is empty</h4>
            <p className="text-muted mb-4">Looks like you haven't added anything yet.</p>
            <button
              className="btn btn-primary btn-lg"
              style={{ background: primaryColor, borderColor: primaryColor, borderRadius: 16 }}
              onClick={() => navigate(`/store/${slug}`)}
            >
              <i className="fas fa-utensils mr-2"></i>Back to Menu
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <Header />
      <div style={{ flex: 1, padding: '24px 0' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* Header */}
              <div className="d-flex align-items-center mb-4">
                <button
                  className="btn btn-outline-secondary btn-sm mr-3"
                  onClick={() => navigate(`/store/${slug}`)}
                >
                  <i className="fas fa-arrow-left mr-2"></i>Back to Menu
                </button>
                <h3 className="font-weight-bold mb-0">Review Your Order</h3>
              </div>

              {/* Reservation mode banner */}
              {isReservationOrder && (
                <div style={{
                  background: '#fff3ec',
                  border: '1px solid rgba(255,87,34,0.3)',
                  borderRadius: 12,
                  padding: '12px 16px',
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}>
                  <i className="fas fa-calendar-check" style={{ color: '#ff5722' }}></i>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    Pre-order for your reservation
                    {reservationContext.dateTime
                      ? ` — ${new Date(reservationContext.dateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(reservationContext.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                      : ''}.
                    Sent to the kitchen when you are seated.
                  </span>
                </div>
              )}

              {/* Store Info */}
              <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body d-flex align-items-center gap-3">
                  {(store.logoUrl || store.design?.logoUrl) && (
                    <img
                      src={store.logoUrl || store.design?.logoUrl}
                      alt={store.locationName}
                      style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover' }}
                    />
                  )}
                  <div>
                    <div className="font-weight-bold" style={{ fontSize: '1.1rem' }}>{store.locationName}</div>
                    <div className="text-muted small">{store.address || store.city}</div>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-3">Items ({cartCount})</h5>
                  {cart.map((item, idx) => {
                    let unit = parseFloat(item.product.price) || 0;
                    item.options.forEach((sel) => {
                      const optDef = productOptions.find((o) => o.id === sel.optionId);
                      if (optDef && optDef.options && optDef.options[sel.choiceIndex]) {
                        unit += parseFloat(optDef.options[sel.choiceIndex].extraFee) || 0;
                      }
                    });
                    return (
                      <div key={idx} className="d-flex align-items-center py-3" style={{ borderBottom: idx < cart.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                        <div style={{ flex: 1 }}>
                          <div className="font-weight-bold">{item.product.name}</div>
                          {item.options.length > 0 && (
                            <div className="text-muted small">
                              {item.options.map((sel) => {
                                const optDef = productOptions.find((o) => o.id === sel.optionId);
                                return optDef?.options?.[sel.choiceIndex]?.name || '';
                              }).filter(Boolean).join(', ')}
                            </div>
                          )}
                          {item.instructions && (
                            <div className="text-muted small" style={{ fontStyle: 'italic' }}>Note: {item.instructions}</div>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{ width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => updateQty(idx, -1)}
                            disabled={item.qty <= 1}
                          >
                            <i className="fas fa-minus" style={{ fontSize: 10 }}></i>
                          </button>
                          <span className="font-weight-bold" style={{ minWidth: 24, textAlign: 'center' }}>{item.qty}</span>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{ width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => updateQty(idx, 1)}
                          >
                            <i className="fas fa-plus" style={{ fontSize: 10 }}></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger ml-2"
                            style={{ width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => removeItem(idx)}
                          >
                            <i className="fas fa-trash" style={{ fontSize: 10 }}></i>
                          </button>
                        </div>
                        <div className="font-weight-bold ml-3" style={{ color: primaryColor, minWidth: 60, textAlign: 'right' }}>
                          {formatCurrency(unit * item.qty)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Type — hidden in reservation mode (always dine_in) */}
              {!isReservationOrder && (
              <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-3">Order Type</h5>
                  <div className="d-flex gap-2">
                    {[
                      { key: 'delivery', label: 'Delivery', icon: 'fa-motorcycle' },
                      { key: 'pickup', label: 'Pick Up', icon: 'fa-store' },
                    ].map((t) => (
                      <button
                        key={t.key}
                        className={`flex-fill py-2 btn ${orderType === t.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={orderType === t.key ? { background: primaryColor, borderColor: primaryColor } : {}}
                        onClick={() => {
                          if (t.key === orderType) return;
                          setOrderType(t.key);
                          // Leaving delivery: surface no stale quote error.
                          if (t.key !== 'delivery') {
                            setQuoteError('');
                            setQuoteOutOfZone(false);
                          }
                          // Back to delivery: reuse the cached quote while
                          // still valid (~15 min), otherwise re-quote.
                          if (t.key === 'delivery' && !quoteStillValid) {
                            quoteDeliveryFee();
                          }
                        }}
                      >
                        <i className={`fas ${t.icon} mr-1`}></i>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {orderType === 'delivery' && (
                    <div className="mt-3">
                      {deliveryAddress ? (
                        <div className="alert alert-light border" style={{ borderRadius: 12 }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <i className="fas fa-map-marker-alt text-orange mr-2"></i>
                              {deliveryAddress}
                            </div>
                            <button
                              className="btn btn-sm btn-link"
                              style={{ color: primaryColor }}
                              onClick={() => setShowNewAddress(!showNewAddress)}
                            >
                              {showNewAddress ? 'Cancel' : 'Change'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-warning" style={{ borderRadius: 12 }}>
                          <i className="fas fa-exclamation-circle mr-2"></i>
                          No delivery address found.
                          <button
                            className="btn btn-sm btn-link p-0 ml-2"
                            style={{ color: primaryColor, fontWeight: 600 }}
                            onClick={() => setShowNewAddress(true)}
                          >
                            Add one now
                          </button>
                        </div>
                      )}
                      {/* New Address Form */}
                      {showNewAddress && (
                        <div className="mt-3 p-3" style={{ background: '#fafafa', borderRadius: 12, border: '1px solid #eee' }}>
                          <h6 className="font-weight-bold mb-2">New Delivery Address</h6>
                          <div className="form-group mb-2">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Street address"
                              value={newAddress.address}
                              onChange={(e) => setNewAddress((p) => ({ ...p, address: e.target.value }))}
                            />
                          </div>
                          <div className="form-row">
                            <div className="form-group col-md-5 mb-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="City"
                                value={newAddress.city}
                                onChange={(e) => setNewAddress((p) => ({ ...p, city: e.target.value }))}
                              />
                            </div>
                            <div className="form-group col-md-4 mb-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="State"
                                value={newAddress.state}
                                onChange={(e) => setNewAddress((p) => ({ ...p, state: e.target.value }))}
                              />
                            </div>
                            <div className="form-group col-md-3 mb-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="ZIP"
                                value={newAddress.zip}
                                onChange={(e) => setNewAddress((p) => ({ ...p, zip: e.target.value }))}
                              />
                            </div>
                          </div>
                          <div className="form-group mb-2">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Label (e.g. Home, Work)"
                              value={newAddress.label}
                              onChange={(e) => setNewAddress((p) => ({ ...p, label: e.target.value }))}
                            />
                          </div>
                          {user?.uid && (
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="saveAddrCheck"
                                checked={saveAddressToProfile}
                                onChange={(e) => setSaveAddressToProfile(e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor="saveAddrCheck" style={{ fontSize: '0.85rem' }}>
                                Save this address to my profile
                              </label>
                            </div>
                          )}
                          <button
                            className="btn btn-sm btn-primary"
                            style={{ background: primaryColor, borderColor: primaryColor }}
                            onClick={handleSaveNewAddress}
                          >
                            Use This Address
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Tip */}
              <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="font-weight-bold mb-0">Tip</h5>
                    <span className="font-weight-bold" style={{ color: primaryColor, fontSize: '1.25rem' }}>{formatCurrency(calculatedTip)}</span>
                  </div>
                  <div className="d-flex gap-2 mb-3">
                    {[10, 15, 20, 25].map((pct) => (
                      <button
                        key={pct}
                        className={`flex-fill btn ${tipIsPercent && parseFloat(tipInput) === pct ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={tipIsPercent && parseFloat(tipInput) === pct ? { background: primaryColor, borderColor: primaryColor } : {}}
                        onClick={() => { setTipIsPercent(true); setTipInput(pct.toString()); }}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                  <div className="d-flex gap-2">
                    <input
                      type="number"
                      className="form-control"
                      placeholder={tipIsPercent ? 'Custom %' : 'Custom $'}
                      value={tipInput}
                      onChange={(e) => setTipInput(e.target.value)}
                    />
                    <button
                      className={`btn ${tipIsPercent ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={tipIsPercent ? { background: primaryColor, borderColor: primaryColor } : {}}
                      onClick={() => setTipIsPercent(true)}
                    >
                      %
                    </button>
                    <button
                      className={`btn ${!tipIsPercent ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={!tipIsPercent ? { background: primaryColor, borderColor: primaryColor } : {}}
                      onClick={() => { setTipIsPercent(false); setTipInput(''); }}
                    >
                      $
                    </button>
                  </div>
                </div>
              </div>

              {/* Order Notes */}
              <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-3">Order Notes</h5>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="Allergies, special requests..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    style={{ borderRadius: 12 }}
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-3">
                    {isReservationOrder ? 'How do you want to pay?' : 'Payment Method'}
                  </h5>
                  <div className="d-flex gap-2 mb-3">
                    <button
                      type="button"
                      className="flex-fill btn btn-outline-secondary text-left"
                      disabled={!hasCash}
                      title={!hasCash ? 'This store does not accept cash payments' : undefined}
                      style={{
                        borderRadius: 12,
                        borderWidth: 2,
                        borderColor: paymentMethod === 'cash' ? primaryColor : undefined,
                        background: paymentMethod === 'cash' ? '#fff3ee' : (!hasCash ? '#f5f5f5' : undefined),
                        color: paymentMethod === 'cash' ? primaryColor : (!hasCash ? '#adadad' : undefined),
                        opacity: !hasCash ? 0.6 : 1,
                        cursor: !hasCash ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => { if (!hasCash) return; setPaymentMethod('cash'); setCardError(''); }}
                    >
                      {!hasCash ? (
                        <i className="fas fa-lock mr-2"></i>
                      ) : (
                        <i className="fas fa-money-bill-wave mr-2"></i>
                      )}
                      {isReservationOrder ? 'Pay at restaurant' : 'Cash'}
                      {!hasCash && (
                        <div style={{ fontSize: '0.72rem', fontWeight: 500, marginTop: 2 }}>
                          Not available at this store
                        </div>
                      )}
                      {isReservationOrder && hasCash && (
                        <div style={{ fontSize: '0.72rem', fontWeight: 500, marginTop: 2, color: '#6b7280' }}>
                          Pay when you arrive
                        </div>
                      )}
                    </button>
                    <button
                      className="flex-fill btn btn-outline-secondary text-left"
                      style={{
                        borderRadius: 12,
                        borderWidth: 2,
                        borderColor: paymentMethod === 'card' ? primaryColor : undefined,
                        background: paymentMethod === 'card' ? '#fff3ee' : undefined,
                        color: paymentMethod === 'card' ? primaryColor : undefined,
                      }}
                      onClick={() => setPaymentMethod('card')}
                    >
                      <i className="fas fa-credit-card mr-2"></i> {isReservationOrder ? 'Pay now (card)' : 'Credit / Debit Card'}
                      {isReservationOrder && (
                        <div style={{ fontSize: '0.72rem', fontWeight: 500, marginTop: 2, color: '#6b7280' }}>
                          Full refund if you cancel before being seated
                        </div>
                      )}
                    </button>
                  </div>
                  {paymentMethod === 'card' && (
                    <div className="p-3" style={{ background: '#fafafa', borderRadius: 12, border: '1px solid #eee' }}>
                      {/* Apple Pay / Google Pay — shown only when the device/browser supports it */}
                      {/* Its own Elements session (mode+amount) — inside the page's
                          bare provider Stripe logs "Unable to select session" and
                          the wallets never mount */}
                      <Elements stripe={stripePromise} options={{ mode: 'payment', amount: Math.max(100, Math.round((finalTotal || 1) * 100)), currency: 'usd' }}>
                        <ExpressCheckoutElement
                          onConfirm={handleWalletConfirm}
                          onReady={(e) => setWalletAvailable(!!(e.availablePaymentMethods?.applePay || e.availablePaymentMethods?.googlePay))}
                          options={{
                            buttonType: { applePay: 'check-out', googlePay: 'pay' },
                            paymentMethods: { applePay: 'always', googlePay: 'always', link: 'never', paypal: 'never', amazonPay: 'never' },
                          }}
                        />
                      </Elements>
                      {walletAvailable && (
                        <div style={{ display: 'flex', alignItems: 'center', margin: '12px 0', color: '#999', fontSize: 12 }}>
                          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }}></div>
                          <span style={{ padding: '0 10px' }}>or pay with card</span>
                          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }}></div>
                        </div>
                      )}
                      {walletBusy && (
                        <div style={{ fontSize: 13, color: '#666', textAlign: 'center', marginBottom: 8 }}>
                          <i className="fas fa-spinner fa-spin mr-1"></i>Processing wallet payment…
                        </div>
                      )}
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', marginBottom: 8, display: 'block' }}>
                        Card Details
                      </label>
                      <CardElement options={cardElementStyle} />
                      {cardError && (
                        <div className="text-danger mt-2" style={{ fontSize: '0.85rem' }}>
                          <i className="fas fa-exclamation-circle mr-1"></i>{cardError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Promo Code */}
              <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body">
                  <h5 className="font-weight-bold mb-2">
                    <i className="fas fa-tags mr-2 text-primary"></i>
                    Promo Code
                  </h5>
                  {appliedPromo ? (
                    <div className="d-flex justify-content-between align-items-center p-2" style={{ background: '#e8f5e9', borderRadius: 8 }}>
                      <div>
                        <div className="font-weight-bold" style={{ color: '#2e7d32' }}>{appliedPromo.code}</div>
                        <small className="text-muted">{appliedPromo.name}</small>
                      </div>
                      <div className="text-right">
                        <div className="font-weight-bold" style={{ color: '#2e7d32' }}>-{formatCurrency(appliedPromo.discountAmount)}</div>
                        <button className="btn btn-sm btn-link text-danger p-0" onClick={handleRemovePromo}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="d-flex" style={{ gap: 8 }}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter promo code"
                          value={promoCodeInput}
                          onChange={(e) => { setPromoCodeInput(e.target.value); setPromoError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyPromo(); } }}
                        />
                        <button
                          className="btn btn-outline-primary"
                          onClick={handleApplyPromo}
                          disabled={applyingPromo || !promoCodeInput.trim()}
                        >
                          {applyingPromo ? 'Checking...' : 'Apply'}
                        </button>
                      </div>
                      {promoError && (
                        <small className="text-danger d-block mt-2">
                          <i className="fas fa-exclamation-circle mr-1"></i>{promoError}
                        </small>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Use Fodyes */}
              {storeFodyesConfig.enabled && userFodyes.backingValue > 0 && (
                <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="font-weight-bold mb-1">
                          <i className="fas fa-coins mr-2 text-warning"></i>
                          Use Fodyes
                        </h5>
                        <span style={{ fontSize: '0.85rem', color: '#666' }}>
                          Tus {userFodyes.balance} Fodyes valen <strong>${userFodyes.backingValue.toFixed(2)}</strong>
                        </span>
                      </div>
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="useFodyesSwitch"
                          checked={useFodyes}
                          onChange={(e) => setUseFodyes(e.target.checked)}
                          disabled={!canUseFodyes}
                        />
                        <label className="custom-control-label" htmlFor="useFodyesSwitch"></label>
                      </div>
                    </div>
                    {!canUseFodyes && minOrderForFodyes > 0 && (
                      <div className="mt-2 p-2" style={{ background: '#ffebee', borderRadius: 8 }}>
                        <span style={{ fontSize: '0.85rem', color: '#c62828' }}>
                          <i className="fas fa-info-circle mr-1"></i>
                          Minimum order of {formatCurrency(minOrderForFodyes)} required to use Fodyes
                        </span>
                      </div>
                    )}
                    {useFodyes && fodyesDiscount > 0 && (
                      <div className="mt-2 p-2" style={{ background: '#fff8e1', borderRadius: 8 }}>
                        <span style={{ fontSize: '0.85rem', color: '#e65100' }}>
                          Descuento aplicado: <strong>-{formatCurrency(fodyesDiscount)}</strong>
                          {maxFodyesDiscount > 0 && fodyesDiscount >= maxFodyesDiscount && (
                            <span className="ml-1">(max {formatCurrency(maxFodyesDiscount)})</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Subtotal ({cartCount} items)</span>
                    <span className="font-weight-bold">{formatCurrency(cartSubtotal)}</span>
                  </div>
                  {taxRate > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Tax ({taxRate}%)</span>
                      <span className="font-weight-bold">{formatCurrency(cartTax)}</span>
                    </div>
                  )}
                  {calculatedTip > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Tip</span>
                      <span className="font-weight-bold">{formatCurrency(calculatedTip)}</span>
                    </div>
                  )}
                  {convenienceFee > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Convenience Fee</span>
                      <span className="font-weight-bold">{formatCurrency(convenienceFee)}</span>
                    </div>
                  )}
                  {/* Uber Direct delivery fee (3PL fulfillment) — quoted live,
                      shows ETA when available. */}
                  {quoteLoading && orderType === 'delivery' && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted"><i className="fas fa-spinner fa-spin mr-1"></i>Quoting delivery…</span>
                    </div>
                  )}
                  {!quoteLoading && deliveryFee > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">
                        Delivery Fee (Uber{deliveryQuote?.durationMinutes ? ` · ~${deliveryQuote.durationMinutes} min` : ''})
                      </span>
                      <span className="font-weight-bold">{formatCurrency(deliveryFee)}</span>
                    </div>
                  )}
                  {quoteError && orderType === 'delivery' && (
                    // Out-of-zone is a hard stop (order blocked); a generic
                    // quote failure degrades softly.
                    <div
                      className="mb-2"
                      style={{
                        fontSize: '0.8rem',
                        fontWeight: quoteOutOfZone ? 600 : 400,
                        color: quoteOutOfZone ? '#b91c1c' : '#c2410c',
                      }}
                    >
                      {quoteError}
                    </div>
                  )}
                  {promoDiscount > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Promo ({appliedPromo?.code})</span>
                      <span className="font-weight-bold" style={{ color: '#2e7d32' }}>-{formatCurrency(promoDiscount)}</span>
                    </div>
                  )}
                  {fodyesDiscount > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Fodyes Discount</span>
                      <span className="font-weight-bold" style={{ color: '#e65100' }}>-{formatCurrency(fodyesDiscount)}</span>
                    </div>
                  )}
                  <hr />
                  <div className="d-flex justify-content-between align-items-center" style={{ fontSize: '1.25rem' }}>
                    <span className="font-weight-bold">Total</span>
                    <span className="font-weight-bold" style={{ color: primaryColor }}>{formatCurrency(finalTotal)}</span>
                  </div>
                  {fodyesEarned > 0 && (
                    <div className="mt-2 text-right">
                      <span style={{ fontSize: '0.85rem', color: '#888' }}>
                        <i className="fas fa-coins mr-1 text-warning"></i>
                        You will earn <strong>{fodyesEarned} Fodyes</strong> with this order
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Place Order */}
              <button
                className="btn btn-primary btn-lg btn-block mb-4"
                style={{ background: primaryColor, borderColor: primaryColor, borderRadius: 16 }}
                disabled={!canPlaceOrder() || placingOrder}
                onClick={handlePlaceOrder}
              >
                {placingOrder ? (
                  <><i className="fas fa-spinner fa-spin mr-2"></i>Placing Order...</>
                ) : isReservationOrder ? (
                  paymentMethod === 'card' ? (
                    <><i className="fas fa-credit-card mr-2"></i>Pay & add to reservation · {formatCurrency(finalTotal)}</>
                  ) : (
                    <><i className="fas fa-calendar-check mr-2"></i>Add to reservation · pay at restaurant</>
                  )
                ) : (
                  <><i className="fas fa-check mr-2"></i>Place Order · {formatCurrency(finalTotal)}</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Processing Overlay */}
      {showProcessing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.92)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <img
            src="/fodyfee_loading.png"
            alt="Processing"
            style={{ width: 120, height: 120, marginBottom: 24 }}
          />
          <h3 style={{ fontWeight: 700, color: '#333', marginBottom: 8 }}>
            Processing your order...
          </h3>
          <p style={{ color: '#666', fontSize: '0.95rem' }}>
            Please do not close this window
          </p>
        </div>
      )}
      <Footer />
    </div>
  );
};

const CheckoutPage = () => (
  <Elements stripe={stripePromise}>
    <CheckoutForm />
  </Elements>
);

export default CheckoutPage;
